import React, { lazy } from 'react';
import { ParallaxProvider } from 'react-scroll-parallax';

import frontofficeDarkThemeOptions from "./themes/frontofficeDarkThemeOptions";
import frontofficeLightThemeOptions from "./themes/frontofficeLightThemeOptions";
import backofficeDarkThemeOptions from "./themes/backofficeDarkThemeOptions";
import backofficeLightThemeOptions from "./themes/backofficeLightThemeOptions";

import AppAuthGuard from "./wrappers/AppAuthGuard";
import OidcAuthGuard from "./wrappers/OidcAuthGuard";
import OidcToReduxSync from "./wrappers/OidcToReduxSync";
import BottomMenuDecorator from "./wrappers/BottomMenuDecorator";
import NotificationToastsDecorator from "./wrappers/NotificationToastsDecorator";
import { OidcUserProvider } from "./contexts/OidcUserContext";
import { ExchangeProvider } from "./contexts/ExchangeContext";
import { SceneProvider } from './contexts/SceneContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';


const StorefrontSimpleLayout = lazy(() => import("./layouts/StorefrontSimpleLayout"));
const StorefrontLayout = lazy(() => import("./layouts/StorefrontLayout"));
const StorefrontInvestmentPartyLayout = lazy(() => import("./layouts/StorefrontInvestmentPartyLayout"));
const StorefrontHomeLayout = lazy(() => import("./layouts/StorefrontHomeLayout"));
const StorefrontContactUsLayout = lazy(() => import("./layouts/StorefrontContactUsLayout"));
const BackofficeLayout = lazy(() => import("./layouts/BackofficeLayout"));
const FrontofficeLayout = lazy(() => import("./layouts/FrontofficeLayout"));
const FrontofficeDiscoverLayout = lazy(() => import("./layouts/FrontofficeDiscoverLayout"));
const FrontofficeInvestmentPartyLayout = lazy(() => import("./layouts/FrontofficeInvestmentPartyLayout"));

export default [
  {
    path: '/',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./views/Login")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [OidcUserProvider],
  },
  {
    path: '/signup',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./views/Signup")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [OidcUserProvider],
  },
  {
    path: '/forgot-password',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./views/ForgotPassword")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/change-password',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./views/ChangePassword")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/delete-account',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./views/ConfirmDeleteUser")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/privacy-policy',
    layout: StorefrontSimpleLayout,
    mainElement: lazy(() => import("./views/PrivacyPolicy")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/home',
    layout: StorefrontHomeLayout,
    mainElement: lazy(() => import("./views/Home")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [ParallaxProvider],
  },
  {
    path: '/about-us',
    layout: StorefrontLayout,
    mainElement: lazy(() => import("./views/AboutUs")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/frontips',
    layout: StorefrontLayout,
    mainElement: lazy(() => import("./views/Frontips")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/community',
    layout: StorefrontLayout,
    mainElement: lazy(() => import("./views/Community")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/partners',
    layout: StorefrontLayout,
    mainElement: lazy(() => import("./views/Partners")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/front-plus',
    layout: StorefrontLayout,
    mainElement: lazy(() => import("./views/FrontPlus")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [ParallaxProvider],
  },
  {
    path: '/frequently-asked-questions',
    layout: StorefrontLayout,
    mainElement: lazy(() => import("./views/Faq")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/contact-us',
    layout: StorefrontContactUsLayout,
    mainElement: lazy(() => import("./views/ContactUs")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/terms-and-conditions',
    layout: StorefrontLayout,
    mainElement: lazy(() => import("./views/TermsAndConditions")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/front-plus-welcome',
    layout: StorefrontLayout,
    mainElement: lazy(() => import("./views/FrontPlusWelcome")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/investment-parties/:investmentPartyId',
    layout: StorefrontInvestmentPartyLayout,
    mainElement: lazy(() => import("./views/InvestmentParty")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/investment-parties-logged-in/:investmentPartyId',
    layout: FrontofficeInvestmentPartyLayout,
    mainElement: lazy(() => import("./views/InvestmentParty")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [],
  },
  {
    path: '/home/:activeTab',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Events")),
    asideElement: lazy(() => import("./views/EventsAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/hashtags/:hashtagText',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Hashtag")),
    asideElement: lazy(() => import("./views/HashtagAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/posts/:postId',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Post")),
    asideElement: lazy(() => import("./views/PostAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [OidcUserProvider, SubscriptionProvider],
  },
  {
    path: '/pending-deposits',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/PendingDeposits")),
    asideElement: lazy(() => import("./views/PendingDepositsAside")),
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
  },
  {
    path: '/wallet/:activeTab',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Wallet")),
    asideElement: lazy(() => import("./views/WalletAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
      SceneProvider,
    ],
  },
  {
    path: '/complete-profile/11',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/InvestmentAccountReadinessWizardFormArgentina")),
    asideElement: lazy(() => import("./views/InvestmentAccountReadinessWizardFormAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/complete-profile/47',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/InvestmentAccountReadinessWizardFormChile")),
    asideElement: lazy(() => import("./views/InvestmentAccountReadinessWizardFormAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/goals/:activeTab',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Goals")),
    asideElement: lazy(() => import("./views/GoalsAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/goals/:goalId/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/Goal")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: (params) => {
      const base = [
        OidcUserProvider,
        OidcToReduxSync,
        OidcAuthGuard,
        AppAuthGuard,
        BottomMenuDecorator,
        NotificationToastsDecorator,
        SubscriptionProvider,
      ];
      return params.activeTab === 'distribution' ? [...base, SceneProvider] : base;
    },
  },
  {
    path: '/edit-goal/:goalId/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/EditGoal")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/no-goal/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/NoGoal")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/advisor',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Advisor")),
    asideElement: lazy(() => import("./views/AdvisorAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/watchlist',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Watchlist")),
    asideElement: lazy(() => import("./views/WatchlistAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/bookmarks',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Bookmarks")),
    asideElement: lazy(() => import("./views/BookmarksAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/cart',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Cart")),
    asideElement: lazy(() => import("./views/CartAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/prices',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/Prices")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/balances',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/Balances")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/investment-items',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/InvestmentItems")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/investment-items/:investmentItemId/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/InvestmentItem")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
      ExchangeProvider,
    ],
  },
  {
    path: '/investors',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/Investors")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/users/:followeeUsername/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/User")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/edit-user/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/EditUser")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/brokers',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/Brokers")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/brokers/:brokerId/:activeTab',
    layout: FrontofficeDiscoverLayout,
    mainElement: lazy(() => import("./views/Broker")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/boosts/:activeTab',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Boosts")),
    asideElement: lazy(() => import("./views/BoostsAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/academy',
    layout: FrontofficeLayout,
    mainElement: lazy(() => import("./views/Academy")),
    asideElement: lazy(() => import("./views/AcademyAside")),
    darkThemeOptions: frontofficeDarkThemeOptions,
    lightThemeOptions: frontofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/edit-user',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/EditUser")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      SubscriptionProvider,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-notifications',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/MyNotifications")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/dashboard',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/MyBrokersDashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/operaciones',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/MyBrokersOperations")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/investment-accounts',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/MyBrokersInvestmentAccounts")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/users',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/MyBrokersUsers")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/prices',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/MyBrokersPrices")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/my-brokers/:brokerId/empleados',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/MyBrokersEmployees")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/cafci/procesos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/CafciDashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/cafci/prices',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/CafciPrecios")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/invertironline/procesos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/InvertirOnlineDashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/invertironline/prices',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/InvertirOnlinePrices")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/bind/procesos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/BindDashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/modulos/bind/transacciones',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/BindTransactions")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/dashboard',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Dashboard")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/procesos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Processes")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/operaciones',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Operations")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/users',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Users")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/campaigns',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Campaigns")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/empleados',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Employees")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/tipos_de_objetivo',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/GoalTypes")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/goals',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Goals")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/boosts',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Boosts")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/balances',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Balances")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/brokers',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Brokers")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/investment-items',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/InvestmentItems")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/investment-accounts',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/InvestmentAccounts")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/planes_de_inversion',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/RiskProfiles")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/investment-parties',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/InvestmentParties")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/prices',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Prices")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/posts',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Posts")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/feriados',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/BankHolidays")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/eventos',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Events")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  },
  {
    path: '/backoffice/notifications',
    layout: BackofficeLayout,
    mainElement: lazy(() => import("./views/backoffice/Notifications")),
    darkThemeOptions: backofficeDarkThemeOptions,
    lightThemeOptions: backofficeLightThemeOptions,
    wrappers: [
      OidcUserProvider,
      OidcToReduxSync,
      OidcAuthGuard,
      AppAuthGuard,
      BottomMenuDecorator,
      NotificationToastsDecorator,
    ],
  }
];